import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import useOdulOneriConfig from '../../hooks/useOdulOneriConfig'
import {
  OPEX_DECISION,
  OPEX_REJECT_REASONS,
  RAMAK_KALA_REASON,
} from '../../helper/odulOneriOpexReasons'
import { RECORD_STATUS } from '../../helper/odulOneriStatus'
import { repairOdulOneriText } from '../../helper/odulOneriText'
import { isOdulOneriOpexAuthorized } from '../../helper/authorizedUsers'
import { REPORT_KEYS } from '../../helper/accessControlConstants'
import {
  clearOpexSession,
  readOpexSession,
} from '../../helper/odulOneriTrackingSession'
import { auth } from '../../auth/firebase'
import './OpexOnFiltreleme.css'

const normalizeEmail = (value) => String(value || '').trim().toLowerCase()

const resolveLoggedInDisplayName = (email, opexUsers = []) => {
  const normalized = normalizeEmail(email)
  if (!normalized) {
    return String(auth.currentUser?.displayName || '').trim()
  }
  const fromOpex = opexUsers.find(
    (u) => String(u.email || '').trim().toLowerCase() === normalized
  )
  if (fromOpex?.fullName) return String(fromOpex.fullName).trim()
  const firebaseName = String(auth.currentUser?.displayName || '').trim()
  if (firebaseName) return firebaseName
  if (!normalized.includes('@')) return normalized
  return normalized
    .split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toLocaleUpperCase('tr-TR') + part.slice(1))
    .join(' ')
}

const OpexOnFiltreleme = () => {
  const { currentUser } = useSelector((state) => state.auth)
  const permissions = useSelector((state) => state.accessControl.permissions)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepRecordId = String(searchParams.get('recordId') || '').trim()
  const accessToken = String(searchParams.get('access') || '').trim()

  const {
    fetchOpexPendingRecords,
    fetchOdulOneriRecordById,
    fetchDepartments,
    submitOpexDecision,
    requestOpexAccess,
    claimTrackingAccess,
  } = useOdulOneriCall()
  const { getOpexUsers } = useOdulOneriConfig()

  const firebaseEmail = normalizeEmail(currentUser)
  const userReports = permissions[firebaseEmail]?.reports
  const isFirebaseOpex =
    Boolean(userReports?.[REPORT_KEYS.ODUL_OPEX]) ||
    isOdulOneriOpexAuthorized(currentUser)

  const [opexSession, setOpexSession] = useState(() => readOpexSession())
  const [claimingAccess, setClaimingAccess] = useState(Boolean(accessToken))
  const [gateEmail, setGateEmail] = useState('')
  const [gateBusy, setGateBusy] = useState(false)
  const [gateMessage, setGateMessage] = useState('')
  const [deepLinkMissing, setDeepLinkMissing] = useState(false)

  const sessionEmail = normalizeEmail(opexSession?.email)
  const actorEmail = firebaseEmail || sessionEmail
  /** Tam kuyruk: Firebase OPEX veya e-posta oturumu */
  const fullAccess = Boolean(isFirebaseOpex || sessionEmail)
  /** Mail linkindeki recordId ile tek kayıt — kapı yok */
  const mailDeepLink = Boolean(deepRecordId)
  const canOpenPage = Boolean(fullAccess || mailDeepLink)

  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState([])
  const [departments, setDepartments] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [decision, setDecision] = useState('')
  const [rejectReasons, setRejectReasons] = useState([])
  const [justification, setJustification] = useState('')
  const [targetDepartmentId, setTargetDepartmentId] = useState('')
  const [forwardNote, setForwardNote] = useState('')
  const [evaluatorName, setEvaluatorName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [clientError, setClientError] = useState('')

  const load = useCallback(async () => {
    if (!canOpenPage) {
      setRecords([])
      setLoading(false)
      return
    }
    setLoading(true)
    setDeepLinkMissing(false)
    try {
      const [depts, opexUsers] = await Promise.all([
        fetchDepartments(),
        getOpexUsers(),
      ])
      setDepartments(depts)
      setEvaluatorName(resolveLoggedInDisplayName(actorEmail, opexUsers))

      if (fullAccess) {
        const list = await fetchOpexPendingRecords()
        setRecords(list)
        setSelectedId((prev) => {
          if (deepRecordId && list.some((r) => r.id === deepRecordId)) return deepRecordId
          if (prev && list.some((r) => r.id === prev)) return prev
          return list[0]?.id || ''
        })
      } else {
        // Yalnızca maildeki kayıt — OPEX bekliyorsa aç
        const one = await fetchOdulOneriRecordById(deepRecordId)
        if (one && one.status === RECORD_STATUS.OPEX_PENDING) {
          setRecords([one])
          setSelectedId(one.id)
        } else {
          setRecords([])
          setSelectedId('')
          setDeepLinkMissing(true)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [
    fetchOpexPendingRecords,
    fetchOdulOneriRecordById,
    fetchDepartments,
    getOpexUsers,
    actorEmail,
    deepRecordId,
    fullAccess,
    canOpenPage,
  ])
  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!accessToken) {
        setClaimingAccess(false)
        return
      }
      setClaimingAccess(true)
      const result = await claimTrackingAccess(accessToken)
      if (!mounted) return
      if (result.ok && result.kind === 'opex') {
        setOpexSession(result.session || readOpexSession())
        const next = new URLSearchParams(searchParams)
        next.delete('access')
        if (result.recordId) next.set('recordId', result.recordId)
        setSearchParams(next, { replace: true })
      } else if (result.ok) {
        // tracking token mistakenly opened filtre — still store opex if possible
        setGateMessage('Bu bağlantı OPEX ön inceleme için değil. E-posta ile OPEX girişi isteyin.')
        const next = new URLSearchParams(searchParams)
        next.delete('access')
        setSearchParams(next, { replace: true })
      } else {
        setGateMessage(
          result.error === 'expired'
            ? 'Bağlantının süresi dolmuş. E-posta ile yeniden giriş isteyin.'
            : result.error === 'used'
              ? 'Bu bağlantı daha önce kullanılmış. E-posta ile yeniden giriş isteyin.'
              : 'Giriş bağlantısı geçersiz.'
        )
        const next = new URLSearchParams(searchParams)
        next.delete('access')
        setSearchParams(next, { replace: true })
      }
      setClaimingAccess(false)
    })()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken])

  useEffect(() => {
    if (claimingAccess) return
    load()
  }, [load, claimingAccess])

  const selected = useMemo(
    () => records.find((r) => r.id === selectedId) || null,
    [records, selectedId]
  )

  useEffect(() => {
    if (!selected) return
    setDecision('')
    setRejectReasons([])
    setJustification('')
    setForwardNote('')
    setClientError('')
    setTargetDepartmentId(selected.relatedDepartmentId || '')
  }, [selectedId, selected?.relatedDepartmentId])

  const toggleRejectReason = (reason) => {
    setRejectReasons((prev) =>
      prev.includes(reason) ? prev.filter((x) => x !== reason) : [...prev, reason]
    )
  }

  const validate = () => {
    if (!decision) return 'Karar seçiniz (ilerlet veya kapat).'
    if (decision === OPEX_DECISION.REJECT) {
      if (!rejectReasons.length) return 'En az bir kapatma nedeni seçiniz.'
      if (!justification.trim()) return 'Gerekçe zorunludur.'
    }
    if (decision === OPEX_DECISION.FORWARD && !targetDepartmentId) {
      return 'Yönlendirilecek bölüm seçiniz.'
    }
    return ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const err = validate()
    if (err) {
      setClientError(err)
      return
    }
    setClientError('')
    setSubmitting(true)
    try {
      const dept = departments.find((d) => d.id === targetDepartmentId)
      const actorName =
        evaluatorName.trim() || resolveLoggedInDisplayName(actorEmail, [])
      const payload =
        decision === OPEX_DECISION.REJECT
          ? {
              result: OPEX_DECISION.REJECT,
              reasons: rejectReasons,
              justification: justification.trim(),
              evaluatorName: actorName,
            }
          : {
              result: OPEX_DECISION.FORWARD,
              targetDepartmentId,
              targetDepartmentName: dept?.name || '',
              note: forwardNote.trim(),
              evaluatorName: actorName,
            }
      await submitOpexDecision(selectedId, payload, actorEmail)
      await load()
      setSelectedId('')
    } catch (_) {
      /* toast */
    } finally {
      setSubmitting(false)
    }
  }

  const handleGateSubmit = async (e) => {
    e.preventDefault()
    setGateBusy(true)
    setGateMessage('')
    try {
      await requestOpexAccess(gateEmail)
      setGateMessage(
        'E-posta OPEX listesindeyse giriş bağlantısı gönderildi. Gelen kutunuzu kontrol edin.'
      )
    } finally {
      setGateBusy(false)
    }
  }

  const handleEmailLogout = () => {
    clearOpexSession()
    setOpexSession(null)
    setRecords([])
    setSelectedId('')
    setGateMessage('')
  }

  const attachments = selected?.currentStateAttachments || []
  const showRamakWarning = rejectReasons.includes(RAMAK_KALA_REASON)
  const applicant = selected?.applicant || {}

  if (claimingAccess) {
    return (
      <div className="odul-opex-filtre">
        <div className="access-gate">
          <p>Giriş doğrulanıyor…</p>
        </div>
      </div>
    )
  }

  if (!canOpenPage) {
    return (
      <div className="odul-opex-filtre">
        <header className="odul-opex-filtre-header">
          <div>
            <span className="eyebrow">BONNA • OPEX</span>
            <h1>OPEX Ön Filtreleme</h1>
            <p>Tüm kuyruğu görmek için e-posta ile giriş yapın.</p>
          </div>
        </header>
        <div className="access-gate">
          <h2>E-posta ile giriş</h2>
          <p>
            Bildirim mailindeki kayda özel bağlantı doğrudan açılır. Tüm bekleyen kayıtları
            görmek için OPEX e-postanızı girin.
            Kurumsal hesabınız varsa{' '}
            <button type="button" className="linkish" onClick={() => navigate('/login')}>
              login
            </button>{' '}
            de kullanabilirsiniz.
          </p>
          <form onSubmit={handleGateSubmit} className="access-gate-form">
            <label>
              E-posta
              <input
                type="email"
                required
                autoComplete="email"
                value={gateEmail}
                onChange={(e) => setGateEmail(e.target.value)}
                placeholder="ornek@bonna.com.tr"
              />
            </label>
            <button type="submit" className="btn-submit" disabled={gateBusy}>
              {gateBusy ? 'Gönderiliyor…' : 'Giriş bağlantısı gönder'}
            </button>
          </form>
          {gateMessage && <div className="access-gate-msg">{gateMessage}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className="odul-opex-filtre">
      <header className="odul-opex-filtre-header">
        <div>
          <span className="eyebrow">BONNA • OPEX</span>
          <h1>OPEX Ön Filtreleme ve Bölüme Yönlendirme</h1>
          <p>
            {fullAccess
              ? 'Gelen önerileri inceleyin; bölüme yönlendirin veya gerekçeyle kapatın.'
              : 'Mail bağlantısındaki başvuru — karar verin veya kapatın.'}
            {actorEmail ? ` · ${actorEmail}` : ''}
          </p>
        </div>
        <div className="top-actions">
          <RouterLink to="/odul-oneri/opex-takip">Uygulama Takip</RouterLink>
          {isFirebaseOpex && (
            <RouterLink to="/odul-oneri/kurul-toplanti">Kurul Toplantısı</RouterLink>
          )}
          {sessionEmail && !firebaseEmail && (
            <button type="button" onClick={handleEmailLogout}>
              Çıkış
            </button>
          )}
          <button type="button" onClick={load}>
            Yenile
          </button>
        </div>
      </header>

      <div className="odul-opex-filtre-layout">
        <aside className="queue">
          <h3>
            {fullAccess ? 'Bekleyen kayıtlar' : 'Maildeki kayıt'} ({records.length})
          </h3>
          {loading && <div className="empty">Yükleniyor…</div>}
          {!loading && records.length === 0 && (
            <div className="empty">
              {deepLinkMissing
                ? 'Bu bağlantıdaki kayıt bulunamadı veya artık OPEX ön incelemede değil.'
                : 'OPEX ön inceleme bekleyen kayıt yok.'}
            </div>
          )}
          {records.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`queue-item${r.id === selectedId ? ' active' : ''}`}
              onClick={() => setSelectedId(r.id)}
            >
              <strong>{r.recordNumber}</strong>
              <span>
                {repairOdulOneriText(r.applicant?.fullName || r.fullName) || '—'} —{' '}
                {repairOdulOneriText(r.topic)}
              </span>
            </button>
          ))}
        </aside>

        <div className="panel-container">
          <div className="header-main">Öneri Ön İnceleme</div>
          <div className="sla-warning">
            Öneri kaydını ön incelemeden geçirin. Uygunsa aksiyon tanımı için ilgili bölüme
            yönlendirin; kapsam dışı veya eksikse gerekçesiyle öneri olarak ilerletmeyin.
          </div>

          {!selected ? (
            <div className="empty">Soldan bir kayıt seçiniz.</div>
          ) : (
            <>
              <div className="section-title">
                Gelen Öneri / Talep Detayları — {selected.recordNumber}
              </div>
              <div className="preview-card">
                <div className="preview-row">
                  <div className="preview-label">Öneri Sahibi:</div>
                  <div className="preview-value">
                    {repairOdulOneriText(applicant.fullName || selected.fullName) || '—'}
                    {(applicant.role || selected.role) &&
                      ` (${repairOdulOneriText(applicant.role || selected.role)})`}
                  </div>
                </div>
                <div className="preview-row">
                  <div className="preview-label">Bölüm / Birim:</div>
                  <div className="preview-value">
                    {repairOdulOneriText(applicant.employeeDept || selected.employeeDept) ||
                      '—'}
                  </div>
                </div>
                <div className="preview-row">
                  <div className="preview-label">Önerinin İlgili Olduğu Bölüm / Birim:</div>
                  <div className="preview-value">
                    {repairOdulOneriText(selected.relatedDepartmentName) || '—'}
                  </div>
                </div>
                <div className="preview-row">
                  <div className="preview-label">Lokasyon:</div>
                  <div className="preview-value">
                    {applicant.location ||
                      selected.location ||
                      selected.facility ||
                      '—'}
                  </div>
                </div>
                <div className="preview-row">
                  <div className="preview-label">Öneri Konusu:</div>
                  <div className="preview-value">
                    {repairOdulOneriText(selected.topic) || '—'}
                  </div>
                </div>
                <div className="preview-row" style={{ flexDirection: 'column', border: 'none' }}>
                  <div className="preview-label" style={{ width: '100%' }}>
                    Mevcut Durum:
                  </div>
                  <div className="preview-value text-block" style={{ width: '100%' }}>
                    {repairOdulOneriText(selected.currentState) || '—'}
                  </div>
                </div>
                <div
                  className="preview-row"
                  style={{ flexDirection: 'column', border: 'none', marginTop: 10 }}
                >
                  <div className="preview-label" style={{ width: '100%' }}>
                    Önerilen Durum:
                  </div>
                  <div className="preview-value text-block" style={{ width: '100%' }}>
                    {repairOdulOneriText(selected.proposedState) || '—'}
                  </div>
                </div>
              </div>

              {attachments.length > 0 && (
                <div className="attachment-card">
                  <div className="attachment-heading">
                    <span>Öneriye Eklenen Fotoğraf / Dokümanlar</span>
                    <span>{attachments.length} dosya</span>
                  </div>
                  <p className="attachment-hint">
                    Öneri sahibi tarafından mevcut durumu açıklamak için eklenen dosyalar.
                  </p>
                  {attachments.map((a) => (
                    <div className="attachment-row" key={a.id || a.url || a.name}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                          {a.name || 'Dosya'}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {a.contentType || a.type || 'Dosya'}
                        </div>
                      </div>
                      {a.url && (
                        <a
                          className="attachment-open"
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Önizle
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="section-title">OPEX Ön Filtreleme ve Yönlendirme</div>
              <form onSubmit={handleSubmit}>
                <div className="field-label">
                  OPEX Ön İnceleme Sonucu <span className="required-star">*</span>
                </div>
                <div className="radio-box-group">
                  <label className="radio-custom-label">
                    <input
                      type="radio"
                      name="opex_sonuc"
                      value={OPEX_DECISION.FORWARD}
                      checked={decision === OPEX_DECISION.FORWARD}
                      onChange={() => {
                        setDecision(OPEX_DECISION.FORWARD)
                        setClientError('')
                      }}
                    />
                    AKSİYON TANIMI İÇİN İLGİLİ BÖLÜME GÖNDER
                  </label>
                  <label className="radio-custom-label">
                    <input
                      type="radio"
                      name="opex_sonuc"
                      value={OPEX_DECISION.REJECT}
                      checked={decision === OPEX_DECISION.REJECT}
                      onChange={() => {
                        setDecision(OPEX_DECISION.REJECT)
                        setClientError('')
                      }}
                    />
                    ÖNERİ OLARAK İLERLETİLMEYECEK
                  </label>
                </div>

                {decision === OPEX_DECISION.REJECT && (
                  <div className="sub-panel">
                    <div className="field-label">
                      Kapatma Nedenleri <span className="required-star">*</span>
                    </div>
                    <div className="check-grid">
                      {OPEX_REJECT_REASONS.map((reason) => (
                        <label key={reason}>
                          <input
                            type="checkbox"
                            checked={rejectReasons.includes(reason)}
                            onChange={() => toggleRejectReason(reason)}
                          />
                          {reason}
                        </label>
                      ))}
                    </div>
                    {showRamakWarning && (
                      <div className="warn-box">
                        Ramak kala / tehlikeli durum bildirimi seçildi. İlgili İSG sürecinin
                        değerlendirilmesi önerilir.
                      </div>
                    )}
                    <div className="form-row">
                      <div className="field-label">
                        Gerekçe <span className="required-star">*</span>
                      </div>
                      <textarea
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        placeholder="Kapatma gerekçesini yazınız…"
                        required
                      />
                    </div>
                  </div>
                )}

                {decision === OPEX_DECISION.FORWARD && (
                  <div className="sub-panel">
                    <div className="form-row">
                      <div className="field-label">
                        Hedef Bölüm <span className="required-star">*</span>
                      </div>
                      <select
                        value={targetDepartmentId}
                        onChange={(e) => setTargetDepartmentId(e.target.value)}
                        required
                      >
                        <option value="">Bölüm seçiniz</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.facility} — {d.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="field-label">OPEX Notu (opsiyonel)</div>
                      <textarea
                        value={forwardNote}
                        onChange={(e) => setForwardNote(e.target.value)}
                        placeholder="Bölüm yöneticisine iletilecek not…"
                      />
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="field-label">Değerlendiren</div>
                  <div className="preview-value" style={{ padding: '10px 0' }}>
                    {evaluatorName || currentUser || '—'}
                  </div>
                </div>

                {clientError && <div className="error-box">{clientError}</div>}

                <button className="btn-submit" type="submit" disabled={submitting || !decision}>
                  {submitting
                    ? 'Gönderiliyor…'
                    : 'Ön Filtrelemeyi Tamamla ve Gönder'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default OpexOnFiltreleme
